import { useState } from "react";
import { useCategoryConfig, useUpdateCategoryConfig } from "@/hooks/useCategoryConfig";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { Edit } from "lucide-react";

const MEASUREMENT_FIELD_OPTIONS = [
  { value: 'area_sqft', label: 'Area (sqft)' },
  { value: 'eave_lf', label: 'Eave (LF)' },
  { value: 'rake_lf', label: 'Rake (LF)' },
  { value: 'valley_lf', label: 'Valley (LF)' },
  { value: 'hip_lf', label: 'Hip (LF)' },
  { value: 'ridge_lf', label: 'Ridge (LF)' },
  { value: 'sidewall_lf', label: 'Sidewall (LF)' },
  { value: 'headwall_lf', label: 'Headwall (LF)' },
  { value: 'flashing_lf', label: 'Flashing (LF)' },
  { value: 'chimney_count', label: 'Chimney Count' },
  { value: 'skylight_count', label: 'Skylight Count' },
  { value: 'pipe_1_5_count', label: 'Pipe 1.5" Count' },
  { value: 'pipe_3_count', label: 'Pipe 3" Count' },
  { value: 'layers', label: 'Layers' },
];

export function CategoryConfigManager() {
  const { data: categories, isLoading } = useCategoryConfig();
  const updateConfig = useUpdateCategoryConfig();
  const [editingId, setEditingId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {categories?.map((config) => (
        <Card key={config.category} className="p-6">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold">{config.display_name}</h3>
                  <Badge variant="outline">{config.category}</Badge>
                </div>
                {config.description && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {config.description}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingId(editingId === config.category ? null : config.category)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Installation Phase</label>
                <Select
                  value={config.install_phase || "none"}
                  onValueChange={(value) => {
                    updateConfig.mutateAsync({
                      id: config.category,
                      install_phase: value === "none" ? null : value,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {INSTALLATION_PHASES.map((phase) => (
                      <SelectItem key={phase.key} value={phase.key}>
                        Phase {phase.number}: {phase.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Combine Strategy</label>
                <Select
                  value={config.combine_strategy || "none"}
                  onValueChange={(value) => {
                    updateConfig.mutateAsync({
                      id: config.category,
                      combine_strategy: value === "none" ? null : (value as any),
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="sum">Sum (Add fields)</SelectItem>
                    <SelectItem value="max">Max (Take highest)</SelectItem>
                    <SelectItem value="individual">Individual (First field)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Allow Multiple</p>
                  <p className="text-xs text-muted-foreground">
                    Can select multiple products
                  </p>
                </div>
                <Switch
                  checked={config.allow_multiple}
                  onCheckedChange={(checked) => {
                    updateConfig.mutateAsync({
                      id: config.category,
                      allow_multiple: checked,
                    });
                  }}
                />
              </div>
            </div>

            {config.measurement_fields && config.measurement_fields.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Measurement Fields</label>
                <div className="flex flex-wrap gap-2">
                  {config.measurement_fields.map((field) => {
                    const option = MEASUREMENT_FIELD_OPTIONS.find(o => o.value === field);
                    return (
                      <Badge key={field} variant="secondary">
                        {option?.label || field}
                      </Badge>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  These measurements control quantity limits for this category
                </p>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
